import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Header } from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Activity, Truck, Wrench, ClipboardCheck, Coins } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const APP_ICONS = {
  ridero: Truck,
  mechanic_kiosk: Wrench,
  pti_pector: ClipboardCheck,
};

const APP_COLORS = {
  ridero: '#3B82F6',
  mechanic_kiosk: '#10B981',
  pti_pector: '#F97316',
};

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [appFilter, setAppFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appFilter]);

  const fetchEvents = async () => {
    try {
      const params = appFilter && appFilter !== 'all' ? `?app_id=${appFilter}` : '';
      const res = await axios.get(`${API}/events${params}`, { withCredentials: true });
      setEvents(res.data);
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setIsLoading(false);
    }
  };

  const getEventBadgeVariant = (eventType) => {
    if (eventType.includes('completed')) return 'default';
    if (eventType.includes('created') || eventType.includes('placed')) return 'secondary';
    if (eventType.includes('failed') || eventType.includes('cancelled')) return 'destructive';
    return 'outline';
  };

  // Group events by date
  const groupedEvents = events.reduce((acc, event) => {
    const date = new Date(event.created_at).toLocaleDateString();
    if (!acc[date]) acc[date] = [];
    acc[date].push(event);
    return acc;
  }, {});

  return (
    <DashboardLayout>
      <Header title="Activity Log" subtitle="Events from all connected apps" />
      
      <div className="p-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card data-testid="total-events-card">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-sm bg-accent/10 flex items-center justify-center">
                <Activity className="w-6 h-6 text-accent" />
              </div>
              <div>
                <p className="text-2xl font-bold">{events.length}</p>
                <p className="text-sm text-muted-foreground">Total Events</p>
              </div>
            </CardContent>
          </Card>
          {Object.entries(APP_ICONS).map(([appId, Icon]) => {
            const count = events.filter(e => e.app_id === appId).length;
            return (
              <Card key={appId} data-testid={`${appId}-events-card`}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div 
                    className="w-12 h-12 rounded-sm flex items-center justify-center"
                    style={{ backgroundColor: `${APP_COLORS[appId]}20` }}
                  >
                    <Icon className="w-6 h-6" style={{ color: APP_COLORS[appId] }} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{count}</p>
                    <p className="text-sm text-muted-foreground">{appId.replace('_', ' ')}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Filter */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">Filter by app:</span>
              <Select value={appFilter} onValueChange={setAppFilter}>
                <SelectTrigger className="w-48" data-testid="app-filter-select">
                  <SelectValue placeholder="All apps" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Apps</SelectItem>
                  <SelectItem value="ridero">Ridero</SelectItem>
                  <SelectItem value="mechanic_kiosk">MechanicKiosk</SelectItem>
                  <SelectItem value="pti_pector">PTI Pector</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Events Timeline */}
        <Card>
          <CardHeader>
            <CardTitle className="font-secondary uppercase">Event Timeline</CardTitle>
            <CardDescription>Real-time activity from connected apps</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8">Loading...</div>
            ) : events.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No events recorded yet. Events will appear here when connected apps send data.
              </div>
            ) : (
              <div className="space-y-6">
                {Object.entries(groupedEvents).map(([date, dateEvents]) => (
                  <div key={date}>
                    <h3 className="text-sm font-medium text-muted-foreground mb-3 sticky top-0 bg-card py-2">
                      {date === new Date().toLocaleDateString() ? 'Today' : date}
                    </h3>
                    <div className="space-y-3">
                      {dateEvents.map((event, idx) => {
                        const Icon = APP_ICONS[event.app_id] || Activity;
                        const color = APP_COLORS[event.app_id] || '#6B7280';
                        
                        return (
                          <div 
                            key={event.event_id}
                            className={`flex gap-4 p-4 rounded-sm bg-secondary/30 border border-border animate-fade-in-up animate-stagger-${Math.min(idx + 1, 6)}`}
                            data-testid={`event-${event.event_id}`}
                          >
                            <div 
                              className="w-10 h-10 rounded-sm flex items-center justify-center shrink-0"
                              style={{ backgroundColor: `${color}20` }}
                            >
                              <Icon className="w-5 h-5" style={{ color }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <Badge variant={getEventBadgeVariant(event.event_type)} className="mb-2">
                                    {event.event_type.replace(/_/g, ' ')}
                                  </Badge>
                                  <p className="text-sm">
                                    <span className="font-medium">{event.app_id}</span>
                                    {event.user_id && (
                                      <span className="text-muted-foreground"> • User: {event.user_id.slice(-8)}</span>
                                    )}
                                  </p>
                                </div>
                                {event.points_earned > 0 && (
                                  <div className="flex items-center gap-1 text-green-500">
                                    <Coins className="w-4 h-4" />
                                    <span className="font-semibold">+{event.points_earned}</span>
                                  </div>
                                )}
                              </div>
                              {Object.keys(event.event_data || {}).length > 0 && (
                                <div className="mt-2 text-xs text-muted-foreground font-mono bg-secondary/50 p-2 rounded">
                                  {JSON.stringify(event.event_data, null, 2)}
                                </div>
                              )}
                              <p className="text-xs text-muted-foreground mt-2">
                                {new Date(event.created_at).toLocaleTimeString()}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
